/**
 * Turning a daily's regeneration plan into what the send composer shows.
 *
 * Pure, and separate from the modal, because this is the thing that must not
 * lie: the composer says "the GC gets a corrected copy" and the server decides
 * independently whether that is true. Both sides run the same rule (the API's
 * `decideAttachment`), so the only way they can disagree is if this label
 * misreads the plan it was handed. Hence unit tests.
 *
 * DELIBERATELY SILENT ON THE COMMON CASE. A daily with no corrections, no
 * foreign blocks, and no code normalization gets NO badge — the composer looks
 * exactly as it did before regeneration existed. Badges appear only when the
 * attachment is not the plain original.
 */
import type {
  PcoGeneratedDocument,
  RollupAttachmentRegeneration,
  RollupSendDocument,
} from '@coffman-tm/shared';

export interface AttachmentPlanBadge {
  /** 'warn' is the missing-signature demotion — the one case that needs eyes. */
  tone: 'neutral' | 'warn';
  label: string;
}

/**
 * The badge for one attachment, or null when there is nothing to say.
 *
 * Order matters: the signature block is checked FIRST, because a daily that
 * wanted regeneration and didn't get it is the only state where what the GC
 * receives differs from what the PM would assume.
 */
export function attachmentPlanBadge(
  regeneration: RollupAttachmentRegeneration | undefined,
): AttachmentPlanBadge | null {
  if (!regeneration) return null;
  if (regeneration.signature_blocked) {
    return { tone: 'warn', label: 'original — signature missing' };
  }
  if (regeneration.plan !== 'regenerated') return null;
  const reasons = regenerationReasons(regeneration);
  return {
    tone: 'neutral',
    label: reasons.length > 0 ? `regenerated — ${reasons.join(', ')}` : 'regenerated',
  };
}

/**
 * Why this daily is being regenerated, compactly, biggest structural reason
 * first: scoping changes what the GC sees on the page, normalization changes
 * the code he reconciles against, corrections change numbers.
 */
export function regenerationReasons(r: RollupAttachmentRegeneration): string[] {
  const out: string[] = [];
  // Trigger (d) leads: it changes the JOB NUMBER at the top of the page, which
  // outranks every other difference for whoever reconciles this package.
  if (r.transferred_from_job) out.push(`moved from job ${r.transferred_from_job}`);
  if (r.scoped) out.push('scoped to this PCO');
  if (r.normalized_block_count > 0) out.push('code normalized');
  if (r.change_count > 0) {
    out.push(`${r.change_count} correction${r.change_count === 1 ? '' : 's'}`);
  }
  return out;
}

/**
 * The PCO Sources tab's "Generated PDF" cell — the text, when the page was
 * built, and whether there is anything to open.
 *
 * Same vocabulary as the composer's badges (`regenerationReasons`) on purpose:
 * the PM reads "scoped to this PCO, 2 corrections" in the send composer before
 * the email goes and again on this row afterwards, and two wordings for one fact
 * would read as two different facts.
 *
 * THE THREE STATES ARE NOT INTERCHANGEABLE.
 *   an artifact          → the reasons it differs from the original, plus the
 *                          date it was built. The date matters because the
 *                          reasons are RE-DERIVED from current data while the
 *                          bytes are frozen — see `pdf/regenerate/sources.ts`.
 *   'sent as original'   → Hoffman got the foreman's untouched page. Said out
 *                          loud rather than left blank, because blank would read
 *                          as "we haven't sent this" — the opposite.
 *   '—'                  → never part of a send on this PCO. Nothing happened,
 *                          and the row should say nothing.
 *
 * Every one of those is a claim about what the GC is holding, so the server
 * hands back `'generated'` / `'original'` only when a send actually delivered
 * the page. A daily missing from the response is `'none'`: the server omits the
 * ones with nothing to report, so absent and explicitly-none must render
 * identically.
 */
export interface GeneratedDocumentCell {
  label: string;
  /** `generated_documents.id` to open beside the original, or null. */
  artifactId: string | null;
  /** ISO timestamp the artifact was rendered, or null. Dates the claim. */
  generatedAt: string | null;
}

/** Rendered when a daily has never been part of a send on this PCO. */
export const GENERATED_CELL_NONE = '—';

export function generatedDocumentCell(
  doc: PcoGeneratedDocument | undefined,
): GeneratedDocumentCell {
  if (!doc || doc.state === 'none') {
    return { label: GENERATED_CELL_NONE, artifactId: null, generatedAt: null };
  }
  if (doc.state === 'original' || doc.artifact === null) {
    // `artifact === null` on a 'generated' row is an impossible response, not a
    // state to invent copy for — fall back to the truthful weaker claim rather
    // than offering a View that would 404.
    return { label: 'sent as original', artifactId: null, generatedAt: null };
  }
  const reasons = regenerationReasons(doc.artifact.regeneration);
  return {
    label: reasons.length > 0 ? reasons.join(', ') : 'regenerated',
    artifactId: doc.artifact.id,
    generatedAt: doc.artifact.created_at,
  };
}

/**
 * How much a Generated PDF column is entitled to say. Shared by both readers
 * of `generatedDocumentCell` — PcoDetailPage.tsx's Sources tab and
 * RollupDetailPage.tsx's week-page mirror.
 *
 * `'—'` in that column MEANS "this report was never part of a send" (Sources
 * tab) or "never part of a send *of this rollup*" (week page, which also
 * filters by `weekly_rollup_id` before this status even applies). Rendering
 * it before the answer arrives, or after the fetch failed, states that as
 * fact on no evidence — so only `'ready'` may print it.
 */
export type GeneratedColumnStatus = 'loading' | 'ready' | 'error';

/**
 * The two documents a Generated PDF column's compare view puts side by side.
 * Shared shape for both readers' `PdfCompareModal` wiring.
 */
export interface SourceComparePair {
  dailyPdfId: string;
  /** `generated_documents.id` — the copy that actually went to the GC. */
  artifactId: string;
  /** Shared by both panes: a send attaches the regenerated copy under the
   *  original's name, so showing two names here would be a lie about what
   *  the GC received. The pane badges are what tell them apart. */
  filename: string;
}

/**
 * The foreman dailies ONE send carried, keyed by `daily_pdf_id` — and, for
 * each, WHICH COPY of it went.
 *
 * Reads a send's per-send document capture (`rollup_sends.documents_snapshot`,
 * migration 0050) — the only record that names both the rollup and the copy
 * that actually rode. `documents: null` is a send that predates the capture and
 * yields an empty map: not recorded is not the same as not sent, so it proves
 * nothing and claims nothing.
 *
 * Non-daily entries (the workbook, vendor invoices) are dropped — this map
 * exists to answer a question about foreman pages.
 */
export function dailyDocumentsInSend(
  send: { documents: RollupSendDocument[] | null } | null | undefined,
): Map<string, RollupSendDocument> {
  const out = new Map<string, RollupSendDocument>();
  for (const doc of send?.documents ?? []) {
    if (doc.kind !== 'daily') continue;
    if (typeof doc.daily_pdf_id === 'string' && doc.daily_pdf_id.length > 0) {
      out.set(doc.daily_pdf_id, doc);
    }
  }
  return out;
}

/**
 * Scopes a PCO-wide generated-documents response down to one rollup's own
 * documents, keyed by `daily_pdf_id` — RollupDetailPage.tsx's week-page
 * guard against cross-week leakage.
 *
 * `GET /api/rollups/pco/:pcoId/generated-documents` answers "which dailies",
 * not "which week": it returns the latest qualifying artifact per
 * `daily_pdf_id` across every rollup the PCO has ever had, because that is
 * what the PCO-wide Sources tab needs. But a block can move between weeks
 * (Transfer Block, Change date, `assignSubmission` all rehome a block into a
 * different submission, which can span a different week), so the same
 * `daily_pdf_id` can carry an artifact from a rollup a block no longer
 * belongs to. Without this filter, the week page a block moved TO would
 * render the artifact (or the `'original'` claim) of the week it moved FROM.
 *
 * TWO SOURCES OF TRUTH, AND **THE CAPTURE OUTRANKS THE ARTIFACT WHEREVER IT
 * SPEAKS.** They answer different questions, and only one of them is about the
 * email the GC is holding right now:
 *
 *   the CAPTURE  — `sentDailies`, the LATEST send's own document list
 *                  (`dailyDocumentsInSend`). It is the RECEIPT for that send:
 *                  it names the rollup and it names which copy of each daily
 *                  went. Authoritative.
 *   an ARTIFACT  — proves only that SOME past send of this rollup carried those
 *                  bytes. Inference, and it goes stale the moment a later send
 *                  attaches something different.
 *
 * The scenario that forces the order: regenerate for send #1, reopen, the
 * trigger goes away (a correction reverted, a foreign block moved off), send #2
 * attaches the ORIGINAL. The artifact from #1 still exists, still names this
 * rollup, and still predates the last send — so artifact-first would print
 * "regenerated" with #1's date over an email that holds the untouched page.
 * That is exactly the misrepresentation this feature exists to end.
 *
 * So, per daily:
 *
 *   capture says `original`     → `'original'`, even when an artifact survives
 *                                 the rollup filter. No compare — there is one
 *                                 copy, and the GC has it.
 *   capture says `regenerated`  → the artifact entry, with the CAPTURE's
 *                                 `generated_document_id` winning if the two
 *                                 disagree: the compare must open the bytes
 *                                 that actually shipped. (They agree in every
 *                                 ordinary flow; the override is the defensive
 *                                 half. The date and reasons still come off the
 *                                 picked row — the standing caveat about this
 *                                 cell, `docs/system/11-intentional.md` §M11,
 *                                 not a new one.)
 *   capture silent / absent     → the artifact filter alone, as before. This
 *                                 covers every send predating migration 0050,
 *                                 which has no capture to read.
 *
 * `'generated'` from the response is kept only when
 * `artifact.weekly_rollup_id === rollupId` — every artifact row carries that
 * column, so the claim is checkable. The response's own `'original'` state is
 * dropped unconditionally: it carries `artifact: null` by construction, so
 * there is no rollup to check it against, and a PCO-wide claim shown on one
 * week's page is the same leak in a smaller shape. Re-establishing it from the
 * capture — attributed to a named send — is what retired
 * `11-intentional.md` M14.
 *
 * `rollupId === null` (no persisted rollup yet for this week) drops the
 * artifacts for free — nothing equals `null` — and such a week has no sends,
 * so `sentDailies` is empty too.
 *
 * A daily absent from the result reads identically once run through
 * `generatedDocumentCell`: `'—'`, the honest "this week never sent it".
 */
export function generatedDocumentsForRollup(
  documents: readonly PcoGeneratedDocument[],
  rollupId: string | null,
  sentDailies: ReadonlyMap<string, RollupSendDocument> = new Map(),
): Map<string, PcoGeneratedDocument> {
  const out = new Map<string, PcoGeneratedDocument>();
  for (const doc of documents) {
    if (doc.artifact?.weekly_rollup_id !== rollupId) continue;
    out.set(doc.daily_pdf_id, doc);
  }
  for (const [dailyPdfId, sent] of sentDailies) {
    if (sent.source === 'original') {
      out.set(dailyPdfId, { daily_pdf_id: dailyPdfId, state: 'original', artifact: null });
      continue;
    }
    if (sent.source !== 'regenerated') continue;
    const held = out.get(dailyPdfId);
    // No surviving artifact to hang the reasons and the date on, so leave the
    // artifact filter's answer alone rather than invent one — the id is all
    // the capture gives us, and a cell with no date beside recomputed reasons
    // it cannot have would be a worse claim than the em dash. TWO PATHS REACH
    // HERE, and they are not the same bug:
    //
    //   1. The daily's blocks have all LEFT this rollup, so the artifact
    //      endpoint omits it entirely. Harmless — the table this map feeds
    //      lists current members, so there is no row to fill.
    //   2. A SIBLING ROLLUP HOLDS A NEWER ARTIFACT for the same daily.
    //      `buildPcoGeneratedDocuments` picks one latest artifact per
    //      `daily_pdf_id` PCO-WIDE, so a multi-PCO or split-week daily whose
    //      other rollup regenerated more recently comes back naming THAT
    //      rollup, and the `weekly_rollup_id` filter above drops it — while
    //      this rollup's blocks are still members and its row IS on screen.
    //      That row reads '—' for a page this week demonstrably rebuilt.
    //
    // Path 2 UNDERSTATES rather than misrepresents, which is the safe
    // direction and why it is accepted here. Fixing it properly means the
    // endpoint returning artifacts per (daily, rollup) instead of collapsing
    // to one per daily — a server change, not another client filter.
    if (!held?.artifact) continue;
    if (sent.generated_document_id && sent.generated_document_id !== held.artifact.id) {
      out.set(dailyPdfId, {
        ...held,
        artifact: { ...held.artifact, id: sent.generated_document_id },
      });
    }
  }
  return out;
}

export interface RegenerationSummary {
  /** Foreman PDFs going as a regenerated copy. */
  regenerated: number;
  /** Foreman PDFs demoted to the original because the signature is unusable. */
  blocked: number;
  /**
   * Of the blocked ones, how many were blocked on a SCOPING trigger — i.e. the
   * original that now goes out still shows another PCO's or another job's work.
   * Tracked separately because it is a different thing to tell the GC about
   * than "these hours are the uncorrected ones".
   */
  blockedScoped: number;
  /** Of the blocked ones, how many had corrections that will not be shown. */
  blockedWithChanges: number;
  /** Of the blocked ones, how many will print under the code as filed. */
  blockedNormalized: number;
  /**
   * Printed job numbers of blocked dailies that were MOVED here from another
   * job. Non-empty means the GC is about to receive a page headed with a job
   * number that is not the one being invoiced — the worst outcome the signature
   * gate can produce, so the callout names it explicitly.
   */
  blockedTransferredJobs: string[];
}

export function summarizeRegeneration(
  attachments: readonly { kind: string; regeneration?: RollupAttachmentRegeneration }[],
): RegenerationSummary {
  let regenerated = 0;
  let blocked = 0;
  let blockedScoped = 0;
  let blockedWithChanges = 0;
  let blockedNormalized = 0;
  const blockedTransferredJobs: string[] = [];
  for (const a of attachments) {
    if (a.kind !== 'pdf' || !a.regeneration) continue;
    const r = a.regeneration;
    if (r.signature_blocked) {
      blocked += 1;
      if (r.scoped) blockedScoped += 1;
      if (r.change_count > 0) blockedWithChanges += 1;
      if (r.normalized_block_count > 0) blockedNormalized += 1;
      if (r.transferred_from_job) blockedTransferredJobs.push(r.transferred_from_job);
    } else if (r.plan === 'regenerated') regenerated += 1;
  }
  return {
    regenerated,
    blocked,
    blockedScoped,
    blockedWithChanges,
    blockedNormalized,
    blockedTransferredJobs,
  };
}

/**
 * The sentences the missing-signature callout should carry, given the summary.
 *
 * Branching matters: `signature_blocked` fires on ANY trigger, so a
 * scoped-with-zero-corrections daily was being described as "has corrections to
 * show" — which is both false and, worse, the wrong warning. The consequence
 * there is not stale hours, it is that the original going out still displays
 * ANOTHER PCO's work to this PCO's reviewer.
 */
export function signatureBlockedCopy(s: RegenerationSummary): string[] {
  if (s.blocked === 0) return [];
  const subject =
    s.blocked === 1 ? 'One foreman PDF above' : `${s.blocked} foreman PDFs above`;
  const its = s.blocked === 1 ? 'its' : 'their';
  const it = s.blocked === 1 ? 'it' : 'them';

  const out = [
    `${subject} could not be rebuilt — we could not read a signature off ${its} original, and an unsigned daily report is not backup the GC can act on. The original is being sent for ${it} instead.`,
  ];
  if (s.blockedScoped > 0) {
    out.push(
      s.blockedScoped === 1 && s.blocked === 1
        ? 'That original also covers work on another PCO or job, and that work will be visible to whoever reviews this package.'
        : 'Some of those originals also cover work on another PCO or job, and that work will be visible to whoever reviews this package.',
    );
  }
  if (s.blockedWithChanges > 0) {
    out.push(
      'Your line corrections are not shown on the attached page — they are still in the workbook, which is what the GC bills from.',
    );
  }
  if (s.blockedNormalized > 0) {
    out.push('Activity codes on those pages print as the foreman filed them.');
  }
  // THE LOUDEST ONE, LAST BEFORE THE CHOICE. Every other consequence here is a
  // detail inside the page; this one is the heading at the top of it, and it
  // names a job that has nothing to do with the invoice it is attached to.
  if (s.blockedTransferredJobs.length > 0) {
    const jobs = [...new Set(s.blockedTransferredJobs)];
    const list = jobs.join(' and ');
    out.push(
      jobs.length === 1 && s.blockedTransferredJobs.length === 1
        ? `That page was moved here from job ${list}, and the original still has job ${list} printed at the top of it — the GC will be reading it against this job's invoice. Rebuilding it is what would have corrected the banner.`
        : `Those pages were moved here from job ${list}, and the originals still have that job printed at the top — the GC will be reading them against this job's invoice. Rebuilding them is what would have corrected the banners.`,
    );
  }
  out.push('Send anyway if that is fine, or cancel and check the source PDF.');
  return out;
}
